import { useMemo, type ReactNode } from 'react'
import { UnreadContext, type UnreadState } from '@/hooks/useUnread'
import { useNotificationUnreadCountSource } from '@/hooks/useNotifications'
import { useInboxConversationsSource } from '@/hooks/useInboxConversations'

/**
 * Den ENE instansen av «ulest» i appen. Bakgrunnen står i `hooks/useUnread.ts`.
 *
 * Den er montert i `AppLayout`, over sidemenyen, bunnmenyen og sidene i
 * `<Outlet>` — alle tre leserne ligger inne i den, og ingen eier tilstanden. Det er
 * hele poenget: en meny kan ikke lenger nullstille sitt eget tall og la de
 * andre stå igjen med sitt.
 *
 * Den ligger med vilje IKKE i roten. Uleste er noe bare en innlogget person
 * har, og `PublicLayout` (delte lenker, offentlige tilbud) skal ikke abonnere på
 * noe som helst.
 */
export function UnreadProvider({ children }: { children: ReactNode }) {
  const notifications = useNotificationUnreadCountSource()
  const inbox = useInboxConversationsSource()

  const value = useMemo<UnreadState>(
    () => ({
      notifications,
      conversations: inbox.conversations,
      groups: inbox.groups,
      // Summert over det innboksen viser, ikke over alt den kjenner til —
      // regelen står der filteret står, i `useInboxConversations`.
      messages: inbox.unreadTotal,
      conversationsLoading: inbox.loading,
      conversationsError: inbox.error,
      markConversationRead: inbox.markRead,
      refetchConversations: inbox.refetch,
    }),
    [
      notifications,
      inbox.unreadTotal,
      inbox.conversations,
      inbox.groups,
      inbox.loading,
      inbox.error,
      inbox.markRead,
      inbox.refetch,
    ]
  )

  return <UnreadContext.Provider value={value}>{children}</UnreadContext.Provider>
}
